import { booleanPointInPolygon } from '@turf/boolean-point-in-polygon'
import { pointToPolygonDistance } from '@turf/point-to-polygon-distance'
import { z } from 'zod'
import { IsoDateTimeSchema, LatitudeSchema, LongitudeSchema } from '../primitives'
import { normalizeCountryIso3 } from './countries'
import { areaBbox, KM_PER_DEGREE, type Area, type AreaGeometry } from './geo'
import { canonicalHash } from './hash'
import { distanceKm } from './identity'

/**
 * Residence evaluation (PLAN v4 §3 as amended by R12–R14). Pure: the API decides with it (policy at registration,
 * live eligibility at CSP time from STORED evidence, R7) and the web app previews with it ("test a point").
 *
 *   evaluateEvidence(evidence, areas)       one piece of evidence vs the union of areas → inside / near / outside /
 *                                           unknown, signed distance to the closest boundary
 *   residenceDecision(evaluations, accepted) strongest ACCEPTED source decides (poa > document > gps > ip) → approve /
 *                                           review / decline + review reason
 */

/* ------------------------------------------------------------------ evidence */

export const RESIDENCE_SOURCES = ['poa', 'document', 'gps', 'ip'] as const
export const ResidenceSourceSchema = z.enum(RESIDENCE_SOURCES)
export type ResidenceSource = z.infer<typeof ResidenceSourceSchema>

/** Strongest first (R14). */
export const RESIDENCE_SOURCE_STRENGTH: readonly ResidenceSource[] = ['poa', 'document', 'gps', 'ip']
/** Sources that only count because the organizer explicitly accepted them (labeled weak in the UI, R14). */
export const WEAK_RESIDENCE_SOURCES: readonly ResidenceSource[] = ['gps', 'ip']

/** Default positional uncertainty per source (km). GPS uses its reported accuracy. */
export const RESIDENCE_UNCERTAINTY_KM: Readonly<Record<ResidenceSource, number>> = {
  poa: 0.2,
  document: 0.2,
  gps: 1,
  ip: 50,
}
/** GPS readings less accurate than this are `unknown` (R13: no clamping). */
export const MAX_GPS_ACCURACY_KM = 5
/** GPS accuracy floor (km). */
export const MIN_GPS_ACCURACY_KM = 0.05

/**
 * One piece of location evidence, stored in `members.assurance.residence.evidence` (raw, so that eligibility can be
 * recomputed live against the CURRENT areas, R7). Never sent to third parties (Nominatim policy, PLAN v4 §9).
 */
export const ResidenceEvidenceSchema = z.object({
  source: ResidenceSourceSchema,
  lat: LatitudeSchema.nullable().default(null),
  lng: LongitudeSchema.nullable().default(null),
  /** Positional accuracy in km (GPS: reported accuracy; others: overrides the source default when known). */
  accuracyKm: z.number().min(0).nullable().default(null),
  /** Country code as reported (ISO2 or ISO3; normalized with normalizeCountryIso3 when evaluated). */
  country: z.string().trim().max(3).nullable().default(null),
  city: z.string().max(200).nullable().default(null),
  region: z.string().max(200).nullable().default(null),
  postalCode: z.string().max(40).nullable().default(null),
  /** IP evidence: VPN / Tor / proxy detected ⇒ unknown (R13). */
  vpn: z.boolean().nullable().default(null),
  /** IP evidence: data-center address ⇒ unknown (R13). */
  datacenter: z.boolean().nullable().default(null),
  /** Test organizations: synthetic evidence derived from the area (R19), labeled in the UI. */
  synthetic: z.boolean().default(false),
  /** Didit session that produced it (poa / document / ip), when any. */
  sessionId: z.string().max(100).nullable().default(null),
  capturedAt: IsoDateTimeSchema.nullable().default(null),
})
export type ResidenceEvidence = z.infer<typeof ResidenceEvidenceSchema>
export type ResidenceEvidenceInput = z.input<typeof ResidenceEvidenceSchema>

/* ------------------------------------------------------------------ evaluation */

export const RESIDENCE_VERDICTS = ['inside', 'near', 'outside', 'unknown'] as const
export const ResidenceVerdictSchema = z.enum(RESIDENCE_VERDICTS)
export type ResidenceVerdict = z.infer<typeof ResidenceVerdictSchema>

/** Union of areas: the best verdict wins; `unknown` beats `outside` (an area we could not evaluate may contain it). */
const VERDICT_RANK: Record<ResidenceVerdict, number> = { inside: 3, near: 2, unknown: 1, outside: 0 }

export const EvidenceEvaluationSchema = z.object({
  source: ResidenceSourceSchema,
  verdict: ResidenceVerdictSchema,
  /** Signed distance to the closest area boundary in km (negative = inside); null without coordinates. */
  distanceKm: z.number().nullable(),
  /** Uncertainty used (km), null when the evidence was not usable. */
  uncertaintyKm: z.number().nullable(),
  /** Index (in `areas`) of the area that gave the verdict. */
  areaIndex: z.number().int().min(0).nullable(),
  method: z.enum(['geometry', 'country_code', 'none']),
  /** Why the verdict is `unknown`, when it is. */
  unknownReason: z
    .enum(['no_location', 'low_accuracy', 'vpn_or_datacenter', 'no_country_area', 'no_areas', 'invalid_area'])
    .nullable(),
  synthetic: z.boolean(),
})
export type EvidenceEvaluation = z.infer<typeof EvidenceEvaluationSchema>

/** Uncertainty (km) of an evidence item, or null when it is unusable (GPS too coarse, IP behind VPN). */
export function evidenceUncertaintyKm(e: Pick<ResidenceEvidence, 'source' | 'accuracyKm' | 'vpn' | 'datacenter'>) {
  if (e.source === 'gps') {
    const acc = e.accuracyKm ?? RESIDENCE_UNCERTAINTY_KM.gps
    if (acc > MAX_GPS_ACCURACY_KM) return null
    return Math.max(acc, MIN_GPS_ACCURACY_KM)
  }
  if (e.source === 'ip') {
    if (e.vpn === true || e.datacenter === true) return null
    return Math.max(e.accuracyKm ?? 0, RESIDENCE_UNCERTAINTY_KM.ip)
  }
  return e.accuracyKm ?? RESIDENCE_UNCERTAINTY_KM[e.source]
}

/** Lower bound of the distance (km) from a point to anything inside a bbox (0 when inside the bbox). */
function bboxDistanceKm(pt: { lat: number; lng: number }, [w, s, e, n]: [number, number, number, number]): number {
  const lat = Math.min(Math.max(pt.lat, s), n)
  const lng = Math.min(Math.max(pt.lng, w), e)
  return lat === pt.lat && lng === pt.lng ? 0 : distanceKm(pt, { lat, lng })
}

/**
 * Signed distance (km) from a point to an area's boundary: negative inside. Circles: haversine. Polygons:
 * @turf/point-to-polygon-distance (geodesic; holes count as outside, MultiPolygons supported). A point far outside
 * the area's bbox returns the bbox lower bound (cheap pre-filter; still > every uncertainty we use).
 */
export function signedDistanceKm(area: Area, pt: { lat: number; lng: number }): number {
  if (area.kind === 'circle') return distanceKm(area.center, pt) - area.radiusKm
  const lower = bboxDistanceKm(pt, areaBbox(area))
  if (lower > 200) return lower
  const geometry = area.geometry as AreaGeometry
  const d = pointToPolygonDistance([pt.lng, pt.lat], geometry as never, { units: 'kilometers' })
  // Guard the sign with an exact point-in-polygon test (distance ≈ 0 on the boundary).
  const inside = booleanPointInPolygon([pt.lng, pt.lat], geometry as never)
  return inside ? -Math.abs(d) : Math.abs(d)
}

/** Extra uncertainty of a simplified boundary (place areas). */
const areaErrorKm = (a: Area) => (a.kind === 'place' ? a.simplifyToleranceDeg * KM_PER_DEGREE : 0)

/** Evaluate ONE evidence item against the union of `areas` (R12/R13). */
export function evaluateEvidence(evidence: ResidenceEvidenceInput, areas: readonly Area[]): EvidenceEvaluation {
  const e = ResidenceEvidenceSchema.parse(evidence)
  const base = { source: e.source, synthetic: e.synthetic }
  const unknown = (unknownReason: EvidenceEvaluation['unknownReason'], method: EvidenceEvaluation['method'] = 'none') =>
    ({
      ...base,
      verdict: 'unknown',
      distanceKm: null,
      uncertaintyKm: null,
      areaIndex: null,
      method,
      unknownReason,
    }) as const
  if (!areas.length) return unknown('no_areas')
  const u = evidenceUncertaintyKm(e)
  if (u === null) return unknown(e.source === 'gps' ? 'low_accuracy' : 'vpn_or_datacenter')

  if (e.lat !== null && e.lng !== null) {
    const pt = { lat: e.lat, lng: e.lng }
    let best: EvidenceEvaluation | null = null
    let invalid = false
    areas.forEach((area, i) => {
      // Never throw on a malformed stored geometry (turf errors, NaN): that area is skipped and, if no other area
      // gives a verdict, the evidence is `unknown` / `invalid_area` (review), never a crash of the CSP / policy.
      let d: number
      let uu: number
      try {
        if (area.kind !== 'circle') {
          // A degenerate geometry (no finite coordinates) yields an inverted / non-finite bbox that the bbox
          // pre-filter would read as "far outside".
          const [w, s, east, n] = areaBbox(area)
          if (![w, s, east, n].every(Number.isFinite) || w > east || s > n) throw new Error('invalid bbox')
        }
        d = signedDistanceKm(area, pt)
        uu = u + areaErrorKm(area)
        if (!Number.isFinite(d) || !Number.isFinite(uu)) throw new Error('non-finite distance')
      } catch {
        invalid = true
        return
      }
      const verdict: ResidenceVerdict = d <= -uu ? 'inside' : d <= uu ? 'near' : 'outside'
      const cand: EvidenceEvaluation = {
        ...base,
        verdict,
        distanceKm: Math.round(d * 1000) / 1000,
        uncertaintyKm: Math.round(uu * 1000) / 1000,
        areaIndex: i,
        method: 'geometry',
        unknownReason: null,
      }
      if (
        !best ||
        VERDICT_RANK[cand.verdict] > VERDICT_RANK[best.verdict] ||
        (cand.verdict === best.verdict && cand.distanceKm! < best.distanceKm!)
      )
        best = cand
    })
    if (!best) return unknown('invalid_area', 'geometry')
    // An unevaluable area could contain the point: `outside` is only trusted when every area was evaluated.
    if (invalid && (best as EvidenceEvaluation).verdict === 'outside') return unknown('invalid_area', 'geometry')
    return best
  }

  // No coordinates: country-code match against country areas only (R12).
  const iso3 = normalizeCountryIso3(e.country)
  if (!iso3) return unknown('no_location')
  const countryAreas = areas.map((a, i) => ({ a, i })).filter(({ a }) => a.kind === 'place' && a.countryIso3)
  if (!countryAreas.length) return unknown('no_country_area')
  const match = countryAreas.find(({ a }) => a.kind === 'place' && a.countryIso3 === iso3)
  // Other (non-country) areas could still contain the evidence ⇒ a mismatch is only `outside` when every area is a
  // country area.
  const verdict: ResidenceVerdict = match ? 'inside' : countryAreas.length === areas.length ? 'outside' : 'unknown'
  return {
    ...base,
    verdict,
    distanceKm: null,
    uncertaintyKm: u,
    areaIndex: match ? match.i : null,
    method: 'country_code',
    unknownReason: verdict === 'unknown' ? 'no_country_area' : null,
  }
}

/* ------------------------------------------------------------------ decision */

/** Which evidence sources the organizer accepts (requirements.residence.evidence). */
export const ResidenceAcceptedSourcesSchema = z.object({
  proofOfAddress: z.boolean().default(true),
  documentAddress: z.boolean().default(false),
  gps: z.boolean().default(false),
  ip: z.boolean().default(false),
})
export type ResidenceAcceptedSources = z.infer<typeof ResidenceAcceptedSourcesSchema>

export const sourceAccepted = (s: ResidenceSource, a: ResidenceAcceptedSources): boolean =>
  s === 'poa' ? a.proofOfAddress : s === 'document' ? a.documentAddress : s === 'gps' ? a.gps : a.ip

export const RESIDENCE_REVIEW_REASONS = [
  'residence_outside',
  'residence_near',
  'residence_unknown',
  'residence_no_evidence',
  'residence_conflict',
] as const
export type ResidenceReviewReason = (typeof RESIDENCE_REVIEW_REASONS)[number]

export const ResidenceDecisionSchema = z.object({
  result: ResidenceVerdictSchema,
  outcome: z.enum(['approve', 'review', 'decline']),
  reason: z.enum(RESIDENCE_REVIEW_REASONS).nullable(),
  /** Source whose verdict decided (strongest accepted source with a known verdict). */
  decidingSource: ResidenceSourceSchema.nullable(),
  /** Decided by GPS / IP only (the organizer accepted a weak source). */
  weak: z.boolean(),
  /** Accepted evidence, evaluated (one per source: the latest of each). */
  evaluations: z.array(EvidenceEvaluationSchema),
})
export type ResidenceDecision = z.infer<typeof ResidenceDecisionSchema>

/**
 * Member outcome from evaluated evidence (R14):
 * - only ACCEPTED sources count; per source the LAST evaluation in the array is used;
 * - the strongest accepted source with a known verdict decides: poa > document > gps > ip;
 * - inside ⇒ approve (GPS / IP: only because the organizer accepted that weak source ⇒ `weak`), unless another
 *   accepted non-IP source says outside ⇒ review `residence_conflict`;
 * - outside: PoA ⇒ decline (final; the organizer can still approve), document / GPS / IP ⇒ review;
 * - near ⇒ review; only unknown verdicts ⇒ review `residence_unknown`; no accepted evidence ⇒ review
 *   `residence_no_evidence`. IP alone never declines.
 */
export function residenceDecision(
  evaluations: readonly EvidenceEvaluation[],
  accepted: ResidenceAcceptedSources
): ResidenceDecision {
  const latest = new Map<ResidenceSource, EvidenceEvaluation>()
  for (const ev of evaluations) if (sourceAccepted(ev.source, accepted)) latest.set(ev.source, ev)
  const used = RESIDENCE_SOURCE_STRENGTH.map((s) => latest.get(s)).filter((e): e is EvidenceEvaluation => !!e)
  const decision = (
    result: ResidenceVerdict,
    outcome: ResidenceDecision['outcome'],
    reason: ResidenceDecision['reason'],
    by: EvidenceEvaluation | null
  ): ResidenceDecision => ({
    result,
    outcome,
    reason,
    decidingSource: by?.source ?? null,
    weak: by ? WEAK_RESIDENCE_SOURCES.includes(by.source) : false,
    evaluations: used,
  })
  if (!used.length) return decision('unknown', 'review', 'residence_no_evidence', null)
  const deciding = used.find((e) => e.verdict !== 'unknown')
  if (!deciding) return decision('unknown', 'review', 'residence_unknown', null)
  switch (deciding.verdict) {
    case 'inside': {
      const conflict = used.some((e) => e !== deciding && e.source !== 'ip' && e.verdict === 'outside')
      return conflict
        ? decision('inside', 'review', 'residence_conflict', deciding)
        : decision('inside', 'approve', null, deciding)
    }
    case 'near':
      return decision('near', 'review', 'residence_near', deciding)
    default:
      return deciding.source === 'poa'
        ? decision('outside', 'decline', 'residence_outside', deciding)
        : decision('outside', 'review', 'residence_outside', deciding)
  }
}

/** Evaluate every evidence item and decide (convenience for the policy and the live eligibility). */
export function decideResidence(
  evidence: readonly ResidenceEvidenceInput[],
  config: { areas: readonly Area[]; evidence: ResidenceAcceptedSources }
): ResidenceDecision {
  return residenceDecision(
    evidence.map((e) => evaluateEvidence(e, config.areas)),
    config.evidence
  )
}

/**
 * Hash binding a manual organizer residence decision to the policy it was taken under (areas + accepted sources,
 * R7): the override only applies while the hash is unchanged.
 */
export function residencePolicyHash(config: { areas: readonly Area[]; evidence: ResidenceAcceptedSources }): string {
  return canonicalHash({ areas: config.areas, evidence: config.evidence })
}

/** Organizer's manual residence decision (review queue), bound to `residencePolicyHash`. */
export const ResidenceManualDecisionSchema = z.object({
  result: z.enum(['inside', 'outside']),
  policyHash: z.string().min(16),
  decidedBy: z.string().nullable(),
  at: IsoDateTimeSchema,
  note: z.string().max(2000).nullable().default(null),
})
export type ResidenceManualDecision = z.infer<typeof ResidenceManualDecisionSchema>
